import SV from 'server-vars';
import { sessionStorage } from 'dibs-browser-storage';
import { getEverLoggedIn, getBuyerId } from 'dibs-cookie-jar';
import tracking from 'dibs-tracking';
import {
    isAbTestVariant,
    getMultivariateTestStatus,
    getAbTestTrackingData,
} from 'dibs-buyer-layout/exports/clientABTestHelpers';
import { PAGE_TYPE, BROWSE_TYPES } from '../../utils/pageConstants';
import { VisitCountModel } from 'dibs-visit-tracking/exports/visitCountModel';
import authModalLoader from '../../utils/AuthModalLoader';
import * as postAuthLoader from './helpers/postAuthLoader';
import isPageType from '../../utils/isPageType';
import { isPlaReferrer } from './helpers/referrerHelper';

const isAllowedPageType = () => {
    const isPdpPrintPage = SV.get('settings.isPrintPDP');
    const allowedPageTypes = [PAGE_TYPE.HOME, PAGE_TYPE.CREATOR, PAGE_TYPE.DEALER, PAGE_TYPE.PDP];

    const isAllowedPage = isPageType([...allowedPageTypes, ...BROWSE_TYPES]);

    return isAllowedPage && !isPdpPrintPage;
};

/**
 * should visitor be prompted to login?
 * @returns {boolean}
 */
const isBlocked = function isBlocked() {
    const searchParams = new URLSearchParams(location.search);
    const loggedIn = getBuyerId(document.cookie);
    if (loggedIn || searchParams.has('modal') || !isAllowedPageType() || isPlaReferrer()) {
        return true;
    }
    return false;
};

const shouldPromptLoginRegistration = (visitCountModel, regPromptLimitOverride) => {
    return regPromptLimitOverride
        ? visitCountModel.get('session') === regPromptLimitOverride
        : visitCountModel.get('session') === visitCountModel.get('regPromptLimit');
};

const canAutoPromptUser = regPromptLimitOverride => {
    const visitCountModel = new VisitCountModel();
    const isModalBlocked = isBlocked();
    const shouldOpenModal = shouldPromptLoginRegistration(visitCountModel, regPromptLimitOverride);
    if (shouldOpenModal && !isModalBlocked) {
        return true;
    } else if (shouldOpenModal && isModalBlocked) {
        visitCountModel.increment('regPromptLimit');
        visitCountModel.save();
        return false;
    } else {
        return false;
    }
};

const getActionAndFlow = () => {
    const { constants } = authModalLoader;
    const action = getEverLoggedIn(document.cookie) ? constants.LOGIN : constants.REGISTER;
    const flow = constants.PAGEVIEW_LIMIT_FLOW;
    return { action, flow };
};

const isRegistrationAction = ({ action, flow }) => {
    return (
        action === authModalLoader.constants.REGISTER &&
        flow === authModalLoader.constants.PAGEVIEW_LIMIT_FLOW
    );
};

/**
 * prompts visitor to register if it's due
 * @return {boolean} true if login prompted
 */
const openLoginModal = () => dispatch => {
    const { action, flow } = getActionAndFlow();

    sessionStorage.setItem('authModalShown', true);
    tracking.trackEvent({
        category: 'promo interaction',
        action: 'auto modal displayed',
        label: 'auto pageview',
    });

    // TODO: clean up once REGISTRATION_AUTO_MODAL AB test is resolved ENGAGE-886
    const isVariant = isAbTestVariant(document.cookie, 'REGISTRATION_AUTO_MODAL');
    const shouldShowRegistrationModal = isRegistrationAction({ action, flow });
    if (shouldShowRegistrationModal) {
        const trackingData = getAbTestTrackingData(document.cookie, 'REGISTRATION_AUTO_MODAL');
        tracking.trackAbTestEvent(trackingData, 'registration auto pageview');
    }

    if (!isVariant && shouldShowRegistrationModal) {
        postAuthLoader.autoPromptRegistration({ flow });
    } else {
        // isVariant true uses default auth modal experience
        authModalLoader.show({
            action: action,
            flow: flow,
            ga: {
                label: 'pageview limit reached',
            },
            fireEntryGa: true,
        });
    }
    dispatch({ type: 'noop' });
};

// TODO: clean up once REGISTRATION_AUTO_MODAL AB test is resolved ENGAGE-886
const VARIANT_1_COUNT_OVERRIDE = 2;
const getRegPromptLimitOverride = () => {
    const { action, flow } = getActionAndFlow();

    const isVariant1 =
        getMultivariateTestStatus(document.cookie, 'REGISTRATION_AUTO_MODAL') === 'VARIANT 1';
    return isRegistrationAction({ action, flow }) && isVariant1 ? VARIANT_1_COUNT_OVERRIDE : null;
};

/**
 * Checks condition for auto prompt user.
 * Opens modal if condition is satisfied.
 * Condition:
 * page view >= 3, // TODO: update once REGISTRATION_AUTO_MODAL AB test is resolved ENGAGE-886
 * home / browse / creator / pdp (not print) page type,
 * User is not logged in and AuthModal is not opened.
 *
 * @return {Promise}
 */
const loginPromptRule = function loginPromptRule() {
    const regPromptLimitOverride = getRegPromptLimitOverride();
    return Promise.resolve(canAutoPromptUser(regPromptLimitOverride));
};

export default {
    name: 'loginPrompt',
    rule: loginPromptRule,
    action: openLoginModal,
    priority: 250,
};
